import ReactDOM from 'react-dom';
import { useEffect, useState } from "react";
import { MapLayerData } from "../services/types";
import { capitalizeWords, formatValue, parseMarkdown } from "../utils/helpers";
import LoadingIndicator from "../components/LoadingIndicator";
import { useIframeContext } from '../context/IframeContext';
import { layersEnv } from '../utils/environment';

interface MapItemDetailsModalProps {
  layerId: string | null;
  pointId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const MapItemDetailsModal = ({ layerId, pointId, isOpen, onClose }: MapItemDetailsModalProps) => {
    const [layerData, setLayerData] = useState<MapLayerData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { isIframe } = useIframeContext();

    const summary = layerData?.summary[0];
    const items = layerData?.items;
    const item = items?.find((item) => Number(item.id) === Number(pointId));

    const name = item?.name;
    const description = item?.description;
    const category = item?.category;
    const value = item?.value;
    const attributes = item?.attributes || [];

    const date = item?.activity_date ? new Date(item.activity_date) : null;
    const formattedDate = date
        ? `${String(date.getUTCMonth() + 1).padStart(2, '0')}/${String(date.getUTCDate()).padStart(2, '0')}/${date.getUTCFullYear()}`
        : '';

    useEffect(() => {
        const fetchLayerData = async () => {
        if (!layerId) return;
        setLoading(true);
        try {
            const response = await fetch(
            `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getlayeritems?layerId=${layerId}&environment=${layersEnv}`,
            {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            }
            );
            if (!response.ok) throw new Error('Failed to fetch layer items');
            const data = await response.json();
            setLayerData(data);
        } catch (error) {
            console.error(`Error fetching layer data for layer ${layerId}:`, error);
            setLayerData(null);
        } finally {
            setLoading(false);
        }
        };

        fetchLayerData();
    }, [layerId]);

    // Hide main scrollbar when the metadata panel is open
    useEffect(() => {
        if (isOpen) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }

        return () => {
          document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle click event for sending point data
    const handleClick = () => {
        const origin = "*";
        // Send message to the parent window
        window.parent.postMessage(
            {
                message: "showProgram",
                data: {
                    id: pointId,
                    layerId
                },
            },
            origin
        );
        console.log("Program point message sent:", { pointId, layerId });
    };

    if (loading) return <LoadingIndicator />;

    return ReactDOM.createPortal(
            <div className={`fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-35 transition-all duration-500 transform ${!isIframe && 'max-w-[456px] mx-auto'}`}>
                <div className={`bg-white w-full shadow-xl overflow-y-auto h-full relative rounded-t-none transition-transform duration-500 transform`}>
                    <div className="sticky top-0 z-10 flex items-center justify-between bg-port-green px-6 py-3">
                        {/* Metric Name as Modal Header */}
                        <div className="text-white text-sm w-full font-bold">
                            {name}
                        </div>
                        <button
                            className="absolute top-1 right-5 text-white text-3xl hover:text-lightGray"
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            &times;
                        </button>
                    </div>
                    {/* Show map location */}
                    <div className='flex justify-end items-center px-6 my-4'>
                        <button onClick={handleClick} className='px-2 py-1 text-xs   bg-white hover:bg-color-cai-blue hover:text-white text-port-text border rounded-md shadow-lg'>
                                Show on Map
                        </button>
                    </div>
                    <div className='p-6 pt-0'>
                        {description && 
                            <p className='text-port-text text-xs w-11/12' dangerouslySetInnerHTML={{ __html: parseMarkdown(description) }}></p>
                        }
                        <div className="my-2">
                            {formattedDate &&
                            <>
                                <p className='text-port-text font-bold text-xs mt-3'>Date</p>
                                <p className='text-port-text text-xs w-11/12'>{formattedDate}</p>
                            </>
                            }
                            <div className='flex w-full mt-6'>
                                {category &&
                                    <div className='flex flex-col w-full'>
                                        <p className='text-port-text font-bold text-xs'>Category</p>
                                        <p className='text-port-text text-xs'> {category}</p>
                                    </div>
                                }
                                {value &&
                                    <div className='flex flex-col w-full'>
                                        <p className='text-port-text font-bold text-xs'>Total</p>
                                        <p className='text-port-text text-xs'> {formatValue(value, summary?.unit ?? 'integer')}</p>
                                        <p className='text-port-text text-[10px]'>{summary?.unit_descriptor}</p>
                                    </div>
                                }
                            </div>
                        </div>
    
                        {/* Table Content */}
                        {attributes.length > 0 &&
                        <>
                            <h3 className="text-port-text text-xs font-bold">Key Attributes</h3>
                            <div className="space-y-4 text-port-text mt-2">
                                <div className="overflow-x-auto rounded-t-md">
                                    <table className="min-w-full border-collapse border border-gray-300 border-t-port-green text-port-text">
                                        <thead>
                                            <tr className="bg-port-green">
                                                <th className="px-4 py-2 text-left text-xs text-white font-semibold">Field</th>
                                                <th className="px-4 py-2 text-left text-xs text-white font-semibold">Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Attributes */}
                                            {attributes?.map((attr) => (
                                                    <tr key={attr.name} className="hover:bg-gray-50">
                                                        <td className="border px-4 py-2 text-xs font-medium">{capitalizeWords(attr.name)}</td>
                                                        <td className="border px-4 py-2 text-xs" dangerouslySetInnerHTML={{ __html: parseMarkdown(attr.value) || "N/A" }}>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                        }
    
                        
                    </div>
                </div>
            </div>,
            document.body
        );
};

export default MapItemDetailsModal;
